import { useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/EmptyState';
import { StatCard } from '@/components/StatCard';
import { StatusBadge } from '@/components/StatusBadge';
import { DataTable, type Column } from '@/components/DataTable';
import { StatsCharts } from '@/components/resources/StatsCharts';
import { api } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { isRunningApp } from '@/lib/appStatus';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { PerContainerStats } from '@/types/app';

export interface StatsTabProps {
  appId: number;
  appStatus: string;
}

const POLL_MS = 30_000;
const RANGES = [
  { value: '1h', label: 'Last hour' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
];
const NO_METRICS = 'Metrics are collected while the app is running. It may be stopped or still deploying.';

const selectClass =
  'h-9 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

const columns: Column<PerContainerStats>[] = [
  {
    key: 'name',
    header: 'Name',
    cell: (c) => <span className="font-mono text-xs">{c.name}</span>,
    sortValue: (c) => c.name,
  },
  { key: 'status', header: 'Status', cell: (c) => <StatusBadge status={c.status} /> },
  {
    key: 'cpu',
    header: 'CPU %',
    cell: (c) => <span className="tabular-nums">{c.cpu_percent.toFixed(1)}%</span>,
    sortValue: (c) => c.cpu_percent,
  },
  {
    key: 'memory',
    header: 'Memory',
    cell: (c) => (
      <span className="tabular-nums">
        {formatBytes(c.memory_used)} / {c.memory_limit > 0 ? formatBytes(c.memory_limit) : 'Unlimited'}
      </span>
    ),
    sortValue: (c) => c.memory_used,
  },
  {
    key: 'health',
    header: 'Health',
    cell: (c) => (c.health_status ? <StatusBadge status={c.health_status} /> : <span className="text-muted-foreground">—</span>),
  },
];

export function StatsTab({ appId, appStatus }: StatsTabProps) {
  const [range, setRange] = useState('1h');

  if (!isRunningApp(appStatus)) {
    return <EmptyState as="h3" title="No metrics right now" description={NO_METRICS} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <select aria-label="Time range" value={range} onChange={(e) => setRange(e.target.value)} className={selectClass}>
          {RANGES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      <StatsBody key={range} appId={appId} range={range} />
    </div>
  );
}

function StatsBody({ appId, range }: { appId: number; range: string }) {
  const { data, error, loading, refetch } = usePolledData(() => api.apps.stats(appId, { range }), POLL_MS);

  if (data === undefined) {
    if (loading) {
      return (
        <div>
          <div aria-hidden className="space-y-4">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
              ))}
            </div>
            <div className="h-64 animate-pulse rounded-xl bg-muted" />
          </div>
          <p role="status" className="sr-only">
            Loading metrics…
          </p>
        </div>
      );
    }
    return (
      <EmptyState
        as="h3"
        title="Could not load metrics"
        description={error ?? undefined}
        action={
          <Button variant="outline" size="sm" onClick={refetch}>
            Retry
          </Button>
        }
      />
    );
  }

  const history = data.history ?? [];
  const perContainer = data.per_container ?? [];
  const current = data.current;

  const stale = error !== null && (
    <div
      role="status"
      className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
    >
      <span>Showing last known metrics — refresh failed.</span>
      <Button variant="ghost" size="xs" onClick={refetch}>
        Retry
      </Button>
    </div>
  );

  if (!current && history.length === 0) {
    return (
      <div className="space-y-4">
        {stale}
        <EmptyState as="h3" icon={BarChart3} title="No metrics yet" description={NO_METRICS} />
      </div>
    );
  }

  const containers = data.containers ?? { total: 0, running: 0, stopped: 0 };
  const hasLimit = !!current && current.memory_limit > 0;

  return (
    <div className="space-y-4">
      {stale}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Containers" value={`${containers.running} / ${containers.total}`} hint="running" />
        <StatCard label="CPU" value={current ? `${current.cpu_percent.toFixed(1)}%` : '—'} />
        <StatCard
          label="Memory"
          value={current ? formatBytes(current.memory_used) : '—'}
          hint={
            current
              ? hasLimit
                ? `of ${formatBytes(current.memory_limit)} · ${current.memory_percent.toFixed(1)}%`
                : 'no limit'
              : undefined
          }
        />
        <StatCard
          label="Network transferred"
          value={current ? `${formatBytes(current.network_in_bytes)} in` : '—'}
          hint={current ? `${formatBytes(current.network_out_bytes)} out · selected range` : undefined}
        />
      </div>
      <Card className="gap-3 p-4">
        <h2 className="font-medium">History</h2>
        <StatsCharts history={history} />
      </Card>
      {perContainer.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-medium">Containers</h2>
          <DataTable<PerContainerStats> columns={columns} rows={perContainer} getRowKey={(c) => c.container_id} />
        </section>
      )}
    </div>
  );
}
